import {
  infiniteQueryOptions,
  queryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { isHTTPError } from "ky";
import { toast } from "sonner";
import * as z from "zod";
import { api } from "../http";
import { paginatedListSchema } from "../utils/zod";
import { participantSchema } from "./participants";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import {
  aiContentDetectionResultSchema,
  piiDetectionResultSchema,
  utteranceTranscriptionWordSchema,
} from "@/routes/_app/meetings/-hooks/utterance/transcription";
import { voiceAuthenticationResultSchema } from "@/routes/_app/meetings/-hooks/utterance/vexon";

export const historyEntrySchema = z.object({
  id: z.uuid(),
  meeting: z.object({
    id: z.uuid(),
    external_id: z.string(),
    meeting_start_time: z.iso.datetime(),
  }),

  duration_ms: z.int(),
  start_time: z.iso.datetime(),
  start_time_ms: z.int(),

  transcription_text: z.string(),
  transcription_words: z.array(utteranceTranscriptionWordSchema),
  created_at: z.iso.datetime(),

  participant: participantSchema,

  ai_results: z
    .object({
      ai_content_detection: aiContentDetectionResultSchema,
      voice_authentication: voiceAuthenticationResultSchema,
      pii: piiDetectionResultSchema,
    })
    .partial(),

  bookmark: z
    .object({
      id: z.uuid(),
      note: z.string().nullable(),
      created_at: z.iso.datetime(),
      updated_at: z.iso.datetime().nullable(),
    })
    .nullable(),

  storage_path: z.string().nullable(),
});
const historyResponseSchema = paginatedListSchema(historyEntrySchema);

export type UtteranceHistoryEntry = z.infer<typeof historyEntrySchema>;
export type UtteranceHistoryResponse = z.infer<typeof historyResponseSchema>;

export async function fetchAllMeetingUtterances(meetingId: string) {
  const utterances: Array<UtteranceHistoryEntry> = [];
  let nextPage: string | null =
    `api/v1/meetings/${meetingId}/utterances?per_page=100&sort=asc`;

  while (nextPage) {
    const page: UtteranceHistoryResponse = await api.processing.raw
      .get(nextPage)
      .json();
    utterances.push(...page.items);
    nextPage = page.next_page;
  }

  return utterances;
}

export function utteranceDetailsQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["utterances", id],
    queryFn() {
      return api.processing.raw
        .get<UtteranceHistoryEntry>(`api/v1/utterances/${id}`)
        .json();
    },
  });
}

export function utteranceHistoryInfiniteQueryOptions(
  meetingId: string,
  opts?: Partial<{
    participant_id: string;
    has_bookmark: boolean;
    per_page: number;
    sort: "asc" | "desc";
  }>,
) {
  return infiniteQueryOptions({
    queryKey: ["meetings", meetingId, "utterances", opts, "infinite"],
    queryFn({ pageParam, client }) {
      const { has_bookmark, per_page, ...stringOpts } = opts ?? {};
      const searchParams = new URLSearchParams(stringOpts);
      if (has_bookmark) searchParams.set("has_bookmark", "true");
      if (per_page) searchParams.set("per_page", per_page.toFixed(0));

      const resPromise = api.processing.raw
        .get<UtteranceHistoryResponse>(pageParam, {
          searchParams,
        })
        .json();

      resPromise.then((res) => {
        res.items.forEach((entry) => {
          client.setQueryData(
            utteranceDetailsQueryOptions(entry.id).queryKey,
            entry,
          );
        });
      });

      return resPromise;
    },
    initialPageParam: `api/v1/meetings/${meetingId}/utterances`,
    getNextPageParam: (lastPage) => lastPage.next_page,
    getPreviousPageParam: (lastPage) => lastPage.previous_page,
  });
}

export function useBookmark(utteranceId: string) {
  const detail = useQuery({
    ...utteranceDetailsQueryOptions(utteranceId),
    select(data) {
      return data.bookmark;
    },
  });

  const create = useMutation({
    mutationFn(note: string | null) {
      return api.processing.raw
        .post(`/api/v1/utterances/${utteranceId}/bookmark`, { json: { note } })
        .json<Exclude<UtteranceHistoryEntry["bookmark"], null>>();
    },
    onSuccess(data, _note, _, { client }) {
      updateBookmarkInUtterance(client, utteranceId, data);
    },
    onError(error) {
      showBookmarkError(error);
    },
  });
  const update = useMutation({
    mutationFn(note: string | null) {
      return api.processing.raw
        .patch(`/api/v1/utterances/${utteranceId}/bookmark`, { json: { note } })
        .json<Exclude<UtteranceHistoryEntry["bookmark"], null>>();
    },
    onSuccess(data, _note, _, { client }) {
      updateBookmarkInUtterance(client, utteranceId, data);
    },
    onError(error) {
      showBookmarkError(error);
    },
  });
  const deleteBookmark = useMutation({
    mutationFn() {
      return api.processing.raw.delete<void>(
        `/api/v1/utterances/${utteranceId}/bookmark`,
      );
    },
    onSuccess(_data, _note, _, { client }) {
      updateBookmarkInUtterance(client, utteranceId, null);
    },
    onError(error) {
      showBookmarkError(error);
    },
  });

  return {
    detail,
    create,
    update,
    delete: deleteBookmark,
  };
}

function showBookmarkError(error: unknown) {
  if (!isHTTPError(error)) {
    toast.error("An unexpected error occurred");
    return;
  }

  const data = (error as { data?: { detail?: string } }).data;
  toast.error(
    typeof data?.detail === "string"
      ? data.detail
      : "An unexpected error occurred",
  );
}

function updateBookmarkInUtterance(
  client: QueryClient,
  utteranceId: string,
  data: UtteranceHistoryEntry["bookmark"],
) {
  const utterance = client.setQueryData(
    utteranceDetailsQueryOptions(utteranceId).queryKey,
    (old) => {
      if (!old) return old;
      else
        return {
          ...old,
          bookmark: data,
        };
    },
  );

  if (utterance) {
    client.setQueriesData<InfiniteData<UtteranceHistoryResponse>>(
      {
        queryKey: ["meetings", utterance.meeting.id, "utterances"],
      },
      (old) => {
        if (!old) return old;
        // TODO: if we end up using immer anywhere else, consider using it here too
        const newState = structuredClone(old);
        newState.pages = old.pages.map((page) => {
          return {
            ...page,
            items: page.items.map((item) =>
              item.id === utteranceId
                ? {
                    ...item,
                    bookmark: data,
                  }
                : item,
            ),
          };
        });

        return newState;
      },
    );

    void client.invalidateQueries({
      queryKey: [
        "meetings",
        utterance.meeting.id,
        "utterances",
        { has_bookmark: true },
      ],
    });
  }
}
